import { useCallback, useEffect, useState } from 'react'

import { useLogger, useWindowDimensions, useHdgwIframeParams } from '@percent/workplace-giving/common/hooks'
import { config } from '@percent/workplace-giving/config/config'
import { Modal, Icon } from '@percent/lemonade'
import classes from './DonationForm.module.scss'
import { DonationFormSkeletonLoader } from './DonationFormSkeletonLoader/DonationFormSkeletonLoader'

export type DonationFormProps = Readonly<{
  url: string
  onClose: () => void
  params?: {
    locale?: string
    primaryColor?: string
  }
  open: boolean
  loading: boolean
  redirectUrl?: string
}>

export function DonationForm({
  url,
  params: { locale, primaryColor } = {},
  onClose,
  open,
  loading,
  redirectUrl
}: DonationFormProps) {
  const { width } = useWindowDimensions()
  const { logError } = useLogger()
  const [showLoader, setShowLoader] = useState(true)
  const [listenerExists, setListenerExists] = useState(false)

  const handleOnClose = useCallback(() => {
    try {
      if (window.parent) {
        window.parent.postMessage(
          {
            message: 'donationWidgetClosed'
          },
          '*'
        )
      }
    } catch (e) {
      logError(e)
    }
    onClose()
  }, [logError, onClose])

  const allowedMessageOrigin = config.allowedMessageOrigins

  useEffect(() => {
    if (loading) {
      setShowLoader(true)
    }
  }, [loading])

  useEffect(() => {
    if (!listenerExists) {
      setListenerExists(true)

      window.addEventListener('message', event => {
        if (!allowedMessageOrigin?.some(origin => ['*', event.origin].includes(origin))) return
        try {
          const eventData = event.data as {
            message: string
            data: {
              amount: number
              currency: string
            }
          }

          if (eventData.message === 'donationFormLoaded') {
            setShowLoader(false)
          }

          if (eventData.message === 'closeDonationWidget') {
            handleOnClose()
          }
        } catch (e) {
          logError(e)
        }
      })
    }
  }, [allowedMessageOrigin, listenerExists, logError, handleOnClose])

  const hdgwParams = useHdgwIframeParams({ locale, primaryColor, redirectUrl })
  const hdgwParamsString = hdgwParams.toString()

  const src = `${url}${hdgwParamsString ? `&${hdgwParamsString}` : ''}`

  return (
    <Modal
      borderRadiiSize="xlarge"
      open={open}
      onClose={handleOnClose}
      width={400}
      isFullscreen={width < 450}
      showModalOverflow={false}
    >
      <div className={classes.closeButtonWrapper}>
        <button
          type="button"
          aria-label="Close"
          style={{ borderRadius: '2rem' }}
          onClick={handleOnClose}
          className={classes.closeButton}
        >
          <Icon name="close" size={4} color="gray600" />
        </button>
      </div>

      {showLoader ? (
        <div className={classes.container}>
          <DonationFormSkeletonLoader />
        </div>
      ) : null}

      {!loading ? (
        <iframe
          title="donation-form"
          style={{
            border: 'none',
            borderRadius: '4px'
          }}
          src={src}
          allow="payment *"
          width={width < 450 ? '100%' : '400px'}
          height="700px"
        />
      ) : null}
    </Modal>
  )
}
